/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-23 19:58:17
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { getFormValues, change } from 'redux-form';
import { Card, TextField, Stack, Button, SkeletonDisplayText, TextStyle } from '@shopify/polaris';
import { CancelSmallMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import empty from 'src/assets/images/filter-tree/empty-option.png';
import { checkValidateIP } from 'src/helpers/analytics-helpers';

type Props = {
  items: any[]
};

type ComposedProps = Props & WithI18nProps;
class IgnoreIPsList extends Component<ComposedProps, State> {
  state = {
    addValue: '',
    error: ''
  };

  render() {
    const { ignoreIPs, loading, i18n } = this.props;
    const { addValue, error } = this.state;

    return (
      <Card.Section>
        <div className="BC__IgnoreIPs-Add">
          <Stack distribution="fillEvenly">
            <div onKeyDown={this.handleKeyPress}>
              <TextField
                value={addValue}
                onChange={this.handleChangeAddValue}
                placeholder={i18n.translate(
                  'AdminHTML.Analytics.Settings.ignoreIpsList_TextFieldPlaceholder'
                )}
                pattern="^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$"
                error={error}
              />
            </div>
            <Button primary onClick={this.handleAddValue}>
              {i18n.translate('AdminHTML.Common.Action.add')}
            </Button>
          </Stack>
        </div>
        {loading ? (
          <ul className="BC__IgnoreIPs-List">
            {[...Array(5).keys()].map((item, key) => (
              <li className="BC__IgnoreIPs-Item" key={key}>
                <SkeletonDisplayText size="small" />
              </li>
            ))}
          </ul>
        ) : (
          !_.isEmpty(ignoreIPs) && (
            <ul className="BC__IgnoreIPs-List">
              {ignoreIPs.map((item, key) => (
                <li className={`BC__IgnoreIPs-Item ${item.isNew && 'is-new'}`} key={key}>
                  <div className="BC__IgnoreIPs--Value">
                    <TextStyle variation="subdued">{item.value}</TextStyle>
                  </div>
                  <div className="BC__IgnoreIPs--Remove">
                    <Button
                      plain
                      onClick={() => this.handleDeleteValue(key)}
                      icon={CancelSmallMinor}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )
        )}

        {_.isEmpty(ignoreIPs) && !loading && (
          <div className="BC-EmptyOption__IgnoreIPs">
            <img src={empty} alt={i18n.translate('AdminHTML.Analytics.Settings.ignoreIpsList_ImageAlt')} />
            <p>{i18n.translate('AdminHTML.Analytics.Settings.ignoreIpsList_EmptyOption')}</p>
          </div>
        )}
      </Card.Section>
    );
  }

  // Hanlde when the enter key is press in Add input
  handleKeyPress = event => {
    const enterKeyPressed = event.keyCode === 13;
    if (enterKeyPressed) {
      event.preventDefault();
      this.handleAddValue();
    }
  };

  // Hanlde change add value
  handleChangeAddValue = value => {
    this.setState({
      addValue: value,
      error: ''
    });
  };

  // Handle add IP
  handleAddValue = () => {
    const { fields, ignoreIPs, i18n } = this.props;
    let { addValue } = this.state;

    const checkedValue = checkValidateIP(addValue, ignoreIPs, i18n);
    if (checkedValue.error) {
      this.setState({ error: checkedValue.error });
    } else {
      const { IP = '' } = checkedValue;
      if (IP) {
        fields.unshift({ value: IP, isNew: true });
      }

      this.setState({
        addValue: '',
        error: ''
      });
    }
  };

  // Handle click delete button
  handleDeleteValue = index => {
    const { fields } = this.props;

    fields.remove(index);
  };
}

const mapStateToProps = (state, props) => {
  const formValues = getFormValues('AnalyticsForm')(state);

  return {
    ignoreIPs: formValues && formValues.ignoreIPs ? formValues.ignoreIPs : {},
    loading: _.get(state.analytics, 'loadingAnalyticsSettings', false)
  };
};

const mapDispatchToProps = dispatch => ({
  updateIgnoreIPs: (index, data) => dispatch(change(`AnalyticsForm`, `ignoreIPs[${index}]`, data))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(IgnoreIPsList));
